import React, { useState, useEffect } from 'react';
import { X, CreditCard, Calendar, Download, CheckCircle, Clock, Crown, Loader2, AlertTriangle } from 'lucide-react';
import './SubscriptionsModal.css';
import { getAllSubscriptions, getPaymentHistory, cancelSubscription, reactivateSubscription } from '../../utils/subscriptionApi';
import { useAuth } from '../../hooks/useAuth';
import ConfirmationDialog from '../ConfirmationDialog';
import AlertModal from '../AlertModal';

interface SubscriptionsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabType = 'current' | 'history';

const SubscriptionsModal: React.FC<SubscriptionsModalProps> = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState<TabType>('current');
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [paymentHistory, setPaymentHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [paymentHistoryLoading, setPaymentHistoryLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentHistoryError, setPaymentHistoryError] = useState<string | null>(null);
  const [canceling, setCanceling] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showReactivateConfirm, setShowReactivateConfirm] = useState(false);
  const [subscriptionToAction, setSubscriptionToAction] = useState<string | null>(null);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertModalConfig, setAlertModalConfig] = useState({
    title: '',
    message: '',
    buttonText: 'OK',
    buttonVariant: 'primary' as 'primary' | 'danger' | 'secondary' | 'success'
  });
  const { user } = useAuth();

  // Fetch data when modal opens
  useEffect(() => {
    if (isOpen && user) {
      fetchSubscriptions();
      fetchPaymentHistory();
    }
  }, [isOpen, user]);

  const fetchSubscriptions = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await getAllSubscriptions();
      console.log('📋 [Frontend] Received subscriptions:', data.subscriptions);
      setSubscriptions(data.subscriptions);
    } catch (err) {
      console.error('Error fetching subscriptions:', err);
      setError('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentHistory = async () => {
    setPaymentHistoryLoading(true);
    setPaymentHistoryError(null);
    
    try {
      const data = await getPaymentHistory();
      setPaymentHistory(data.payments);
    } catch (err) {
      console.error('Error fetching payment history:', err);
      setPaymentHistoryError('Failed to load payment history');
    } finally {
      setPaymentHistoryLoading(false);
    }
  };

  // Helper function to show alert modal
  const showAlert = (title: string, message: string, buttonVariant: 'primary' | 'danger' | 'secondary' | 'success' = 'primary') => {
    setAlertModalConfig({
      title,
      message,
      buttonText: 'OK',
      buttonVariant
    });
    setShowAlertModal(true);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'text-emerald-600 bg-emerald-100';
      case 'canceled':
        return 'text-red-600 bg-red-100';
      case 'past_due':
        return 'text-yellow-600 bg-yellow-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'Basic':
        return 'text-blue-600 bg-blue-100';
      case 'Pro':
        return 'text-emerald-600 bg-emerald-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case 'succeeded':
        return 'text-emerald-600 bg-emerald-100';
      case 'pending':
        return 'text-yellow-600 bg-yellow-100';
      case 'failed':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const handleDownloadInvoice = (invoiceUrl: string, paymentId: string) => {
    window.open(invoiceUrl, '_blank');
  };

  const handleCancelSubscription = async (subscriptionId: string) => {
    setSubscriptionToAction(subscriptionId);
    setShowCancelConfirm(true);
  };

  const handleReactivateSubscription = async (subscriptionId: string) => {
    setSubscriptionToAction(subscriptionId);
    setShowReactivateConfirm(true);
  };

  const handleConfirmCancel = async () => {
    if (!subscriptionToAction) return;

    setCanceling(true);
    setShowCancelConfirm(false);
    
    try {
      await cancelSubscription(true); // Cancel at period end
      await fetchSubscriptions(); // Refresh data
      // Success message will be shown by the UI update
    } catch (err) {
      console.error('Error canceling subscription:', err);
      showAlert(
        'Cancellation Failed',
        'Failed to cancel subscription. Please try again.',
        'danger'
      );
    } finally {
      setCanceling(false);
      setSubscriptionToAction(null);
    }
  };

  const handleConfirmReactivate = async () => {
    if (!subscriptionToAction) return;

    setReactivating(true);
    setShowReactivateConfirm(false);
    
    try {
      await reactivateSubscription();
      await fetchSubscriptions(); // Refresh data
      // Success message will be shown by the UI update
    } catch (err) {
      console.error('Error reactivating subscription:', err);
      showAlert(
        'Reactivation Failed',
        'Failed to reactivate subscription. Please try again.',
        'danger'
      );
    } finally {
      setReactivating(false);
      setSubscriptionToAction(null);
    }
  };

  const handleCancelConfirm = () => {
    setShowCancelConfirm(false);
    setShowReactivateConfirm(false);
    setSubscriptionToAction(null);
  };

  if (!isOpen) return null;

  return (
    <div className="subscriptions-modal-overlay" onClick={onClose}>
      <div className="subscriptions-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="subscriptions-modal-header">
          <div className="flex items-center space-x-3">
            <CreditCard className="w-6 h-6 text-emerald-600" />
            <h2 className="text-xl font-semibold text-slate-900">My Subscriptions</h2>
          </div>
          <button
            onClick={onClose}
            className="subscriptions-modal-close"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="subscriptions-modal-tabs">
          <button
            className={`subscriptions-tab ${activeTab === 'current' ? 'active' : ''}`}
            onClick={() => setActiveTab('current')}
          >
            Current Subscription
          </button>
          <button
            className={`subscriptions-tab ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            Payment History
          </button>
        </div>

        {/* Tab Content */}
        <div className="subscriptions-modal-content">
          {activeTab === 'current' && (
            <div className="current-subscription-tab">
              {loading && (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-emerald-600 animate-spin mr-3" />
                  <span className="text-slate-600">Loading subscriptions...</span>
                </div>
              )}
              
              {error && (
                <div className="text-center py-12">
                  <p className="text-red-600 mb-4">{error}</p>
                  <button 
                    onClick={fetchSubscriptions}
                    className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                  >
                    Try Again
                  </button>
                </div>
              )}
              
              {!loading && !error && subscriptions.length === 0 && (
                <div className="text-center py-12">
                  <CreditCard className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                  <p className="text-slate-600 mb-2">No subscriptions found</p>
                  <p className="text-sm text-slate-500">You haven't subscribed to any plans yet.</p>
                </div>
              )}
              
              {!loading && !error && subscriptions.length > 0 && (
                <div className="space-y-4">
                  {subscriptions.map((subscription) => (
                    <div key={subscription.id} className="subscription-card">
                      {/* Header */}
                      <div className="subscription-card-header">
                        <div className="flex items-center space-x-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getTierColor(subscription.tier)}`}>
                            {subscription.tier === 'Pro' ? (
                              <Crown className="w-5 h-5" />
                            ) : (
                              <CreditCard className="w-5 h-5" />
                            )}
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-900">{subscription.tier} Plan</h3>
                            <p className="text-sm text-slate-500">{subscription.billingCycle} billing</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(subscription.status)}`}>
                            {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
                          </span>
                          {subscription.isActive && (
                            <CheckCircle className="w-4 h-4 text-emerald-500" />
                          )}
                        </div>
                      </div>

                      {/* Details */}
                      <div className="subscription-card-details">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="detail-item">
                            <div className="flex items-center space-x-2 text-slate-500">
                              <Calendar className="w-4 h-4" />
                              <span className="text-sm">Start Date</span>
                            </div>
                            <p className="font-medium text-slate-900">{formatDate(subscription.startDate)}</p>
                          </div>
                          
                          <div className="detail-item">
                            <div className="flex items-center space-x-2 text-slate-500">
                              <Clock className="w-4 h-4" />
                              <span className="text-sm">Next Billing</span>
                            </div>
                            <p className="font-medium text-slate-900">{formatDate(subscription.nextBillingDate)}</p>
                          </div>
                        </div>
                        
                        <div className="detail-item">
                          <div className="flex items-center space-x-2 text-slate-500">
                            <CreditCard className="w-4 h-4" />
                            <span className="text-sm">Price</span>
                          </div>
                          <p className="font-medium text-slate-900">{subscription.price}</p>
                        </div>
                      </div>

                      {/* Cancellation Status */}
                      {subscription.cancelAtPeriodEnd && (
                        <div className="flex items-center space-x-2 text-yellow-600 bg-yellow-50 p-3 rounded-lg mb-4">
                          <Clock className="w-4 h-4" />
                          <span className="text-sm font-medium">
                            {subscription.currentPeriodEnd ? 
                              `Will cancel on ${formatDate(subscription.currentPeriodEnd)}` :
                              'Scheduled for cancellation'
                            }
                          </span>
                        </div>
                      )}

                      {/* Actions */}
                      {subscription.isActive && (
                        <div className="subscription-card-actions">
                          {subscription.cancelAtPeriodEnd ? (
                            <button 
                              className="subscription-action-btn primary"
                              onClick={() => handleReactivateSubscription(subscription.id)}
                              disabled={reactivating}
                            >
                              {reactivating ? (
                                <>
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  <span>Reactivating...</span>
                                </>
                              ) : (
                                <span>Reactivate Subscription</span>
                              )}
                            </button>
                          ) : (
                            <button 
                              className="subscription-action-btn secondary"
                              onClick={() => handleCancelSubscription(subscription.id)}
                              disabled={canceling}
                            >
                              {canceling ? (
                                <>
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  <span>Canceling...</span>
                                </>
                              ) : (
                                <span>Cancel Subscription</span>
                              )}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'history' && (
            <div className="payment-history-tab">
              {paymentHistoryLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-emerald-600 animate-spin mr-3" />
                  <span className="text-slate-600">Loading payment history...</span>
                </div>
              ) : paymentHistoryError ? (
                <div className="text-center py-12">
                  <p className="text-red-600 mb-4">{paymentHistoryError}</p>
                  <button 
                    onClick={fetchPaymentHistory}
                    className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                  >
                    Try Again
                  </button>
                </div>
              ) : paymentHistory.length === 0 ? (
                <div className="text-center py-12">
                  <CreditCard className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                  <p className="text-slate-600 mb-2">No payment history found</p>
                  <p className="text-sm text-slate-500">You haven't made any payments yet.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {paymentHistory.map((payment) => (
                    <div key={payment.id} className="payment-card">
                      {/* Header */}
                      <div className="payment-card-header">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                            <CreditCard className="w-5 h-5 text-emerald-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-900">{payment.description}</h3>
                            <p className="text-sm text-slate-500">{formatDate(payment.date)}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPaymentStatusColor(payment.status)}`}>
                            {payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}
                          </span>
                          <span className="font-semibold text-slate-900">
                            {formatCurrency(payment.amount, payment.currency)}
                          </span>
                        </div>
                      </div>

                      {/* Details */}
                      <div className="payment-card-details">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="detail-item">
                            <div className="flex items-center space-x-2 text-slate-500">
                              <span className="text-sm">Payment ID</span>
                            </div>
                            <p className="font-mono text-sm text-slate-900">{payment.id}</p>
                          </div>
                          
                          <div className="detail-item">
                            <div className="flex items-center space-x-2 text-slate-500">
                              <span className="text-sm">Subscription ID</span>
                            </div>
                            <p className="font-mono text-sm text-slate-900">{payment.subscriptionId || 'N/A'}</p>
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="payment-card-actions">
                        {payment.invoiceUrl ? (
                          <button 
                            className="payment-action-btn"
                            onClick={() => handleDownloadInvoice(payment.invoiceUrl!, payment.id)}
                          >
                            <Download className="w-4 h-4 mr-2" />
                            {payment.invoiceUrl.includes('dashboard.stripe.com/test') ? 'View Payment' : 
                             payment.invoiceUrl.includes('receipt.stripe.com') ? 'View Receipt' : 'Download Invoice'}
                          </button>
                        ) : (
                          <button 
                            className="payment-action-btn disabled"
                            disabled
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Invoice Not Available
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Dialogs */}
      <ConfirmationDialog
        isOpen={showCancelConfirm}
        title="Cancel Subscription"
        message="Are you sure you want to cancel your subscription? You will continue to have access until the end of your current billing period."
        confirmText="Cancel Subscription"
        cancelText="Keep Subscription"
        confirmVariant="danger"
        onConfirm={handleConfirmCancel}
        onCancel={handleCancelConfirm}
        icon={<AlertTriangle className="w-5 h-5" />}
      />

      <ConfirmationDialog
        isOpen={showReactivateConfirm}
        title="Reactivate Subscription"
        message="Are you sure you want to reactivate your subscription? Your subscription will continue as normal."
        confirmText="Reactivate"
        cancelText="Keep Canceled"
        confirmVariant="primary"
        onConfirm={handleConfirmReactivate}
        onCancel={handleCancelConfirm}
        icon={<CheckCircle className="w-5 h-5" />}
      />

      {/* Alert Modal */}
      {showAlertModal && (
        <AlertModal
          isOpen={showAlertModal}
          title={alertModalConfig.title}
          message={alertModalConfig.message}
          buttonText={alertModalConfig.buttonText}
          buttonVariant={alertModalConfig.buttonVariant}
          onClose={() => setShowAlertModal(false)}
        />
      )}
    </div>
  );
};

export default SubscriptionsModal;
